import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Spinner,
  Text,
  IconButton,
  Button,
  HStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiRefreshCw, FiAlertCircle, FiExternalLink } from "react-icons/fi";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase/firebase";
import { ChakraIcon } from "./ChakraIcon";

// Cache to store resolved URLs to prevent redundant network hits
const resolvedUrlCache: Record<string, string> = {};

interface ResolveResult {
  resolvedUrl: string | null;
  isLoading: boolean;
  error: Error | null;
}

// Maps technical Firebase error codes and network errors to clean, user-friendly messages
const getFriendlyErrorMessage = (error: any) => {
  if (!error) return "Failed to load media";

  const errCode = error.code || "";
  const errMessage = error.message || "";

  if (errCode === "storage/object-not-found" || errMessage.includes("object-not-found") || errMessage.includes("does not exist")) {
    return "Media not found or was deleted";
  }
  if (errCode === "storage/unauthorized" || errMessage.includes("unauthorized")) {
    return "Access denied (unauthorized)";
  }
  if (errCode === "storage/canceled" || errMessage.includes("canceled")) {
    return "Load canceled";
  }
  if (errMessage.includes("ERR_NAME_NOT_RESOLVED") || errMessage.includes("network")) {
    return "Network error: Connection blocked or offline";
  }
  if (errMessage.includes("QUIC")) {
    return "Network protocol error. Try clicking Retry or Open Tab.";
  }

  return "Failed to load. Click Retry.";
};

export const useResolveStorageUrl = (urlOrPath?: string, retryTrigger: number = 0): ResolveResult => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!urlOrPath) {
      setResolvedUrl(null);
      return;
    }

    // Standard HTTP URLs that are not Firebase Storage can be used as-is
    if (
      urlOrPath.startsWith("http") &&
      !urlOrPath.includes("firebasestorage.googleapis.com")
    ) {
      setResolvedUrl(urlOrPath);
      setError(null);
      return;
    }

    // If cached and we are not retrying, return immediately
    if (resolvedUrlCache[urlOrPath] && retryTrigger === 0) {
      setResolvedUrl(resolvedUrlCache[urlOrPath]);
      setError(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const resolve = async () => {
      try {
        const sRef = storageRef(storage, urlOrPath);
        const downloadUrl = await getDownloadURL(sRef);
        resolvedUrlCache[urlOrPath] = downloadUrl;
        if (isMounted) {
          setResolvedUrl(downloadUrl);
        }
      } catch (err: any) {
        console.error("Error resolving storage URL:", err, "for path:", urlOrPath);
        if (isMounted) {
          setError(err);
          // Fallback to original URL if it starts with http
          if (urlOrPath.startsWith("http")) {
            setResolvedUrl(urlOrPath);
          } else {
            setResolvedUrl(null);
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    resolve();

    return () => {
      isMounted = false;
    };
  }, [urlOrPath, retryTrigger]);

  return { resolvedUrl, isLoading, error };
};

// ==========================================
// 1. RESOLVED IMAGE COMPONENT
// ==========================================
interface ResolvedImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "onError"> {
  urlOrPath: string;
  fallbackSrc?: string;
}

export const ResolvedImage: React.FC<ResolvedImageProps> = ({
  urlOrPath,
  fallbackSrc = "https://placehold.co/300x300?text=Image+Not+Found",
  alt,
  style,
  ...props
}) => {
  const [retryTrigger, setRetryTrigger] = useState(0);
  const { resolvedUrl, isLoading, error } = useResolveStorageUrl(urlOrPath, retryTrigger);
  const [imgLoadError, setImgLoadError] = useState(false);

  const errorBg = useColorModeValue("gray.50", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    setImgLoadError(false);
  }, [urlOrPath, retryTrigger]);

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRetryTrigger((prev) => prev + 1);
  };

  const handleOpenExternal = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(resolvedUrl || urlOrPath, "_blank");
  };

  if (isLoading) {
    const loaderStyle = {
      width: style?.width || props.width || "200px",
      height: style?.height || props.height || "150px",
      maxWidth: style?.maxWidth || "100%",
      maxHeight: style?.maxHeight || "300px",
      ...style,
      display: "flex", // Force flex display to keep spinner centered, overriding block display
    };
    return (
      <Flex
        align="center"
        justify="center"
        style={loaderStyle}
        bg={errorBg}
        borderRadius="md"
        border="1px solid"
        borderColor={borderColor}
        mx="auto"
      >
        <Spinner size="md" color="zs.accent" />
      </Flex>
    );
  }

  if (error || imgLoadError || !resolvedUrl) {
    return (
      <Box
        p={3}
        bg={errorBg}
        borderRadius="md"
        border="1px solid"
        borderColor="red.300"
        maxW="100%"
        w="300px"
        onClick={(e) => e.stopPropagation()}
        mx="auto"
      >
        <Flex align="center" direction="column" justify="center" textAlign="center" py={2}>
          <ChakraIcon as={FiAlertCircle} boxSize="28px" color="red.400" mb={2} />
          <Text fontSize="xs" fontWeight="semibold" mb={1}>
            Failed to Load Image
          </Text>
          <Text fontSize="10px" color="gray.500" mb={3} maxW="90%" textAlign="center">
            {getFriendlyErrorMessage(error)}
          </Text>
          <HStack spacing={2}>
            <Button
              size="xs"
              leftIcon={<ChakraIcon as={FiRefreshCw} />}
              onClick={handleRetry}
              colorScheme="blue"
              variant="outline"
            >
              Retry
            </Button>
          </HStack>
        </Flex>
      </Box>
    );
  }

  return (
    <img
      src={resolvedUrl}
      alt={alt}
      style={style}
      onError={() => {
        setImgLoadError(true);
      }}
      {...props}
    />
  );
};

// ==========================================
// 2. RESOLVED VIDEO COMPONENT
// ==========================================
interface ResolvedVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  urlOrPath: string;
}

export const ResolvedVideo: React.FC<ResolvedVideoProps> = ({
  urlOrPath,
  style,
  ...props
}) => {
  const [retryTrigger, setRetryTrigger] = useState(0);
  const { resolvedUrl, isLoading, error } = useResolveStorageUrl(urlOrPath, retryTrigger);
  const [videoLoadError, setVideoLoadError] = useState(false);

  const errorBg = useColorModeValue("gray.50", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    setVideoLoadError(false);
  }, [urlOrPath, retryTrigger]);

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRetryTrigger((prev) => prev + 1);
  };

  const handleOpenExternal = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(resolvedUrl || urlOrPath, "_blank");
  };

  if (isLoading) {
    const loaderStyle = {
      width: style?.width || props.width || "200px",
      height: style?.height || props.height || "150px",
      maxWidth: style?.maxWidth || "100%",
      maxHeight: style?.maxHeight || "300px",
      ...style,
      display: "flex", // Force flex display to keep spinner centered
    };
    return (
      <Flex
        align="center"
        justify="center"
        style={loaderStyle}
        bg={errorBg}
        borderRadius="md"
        border="1px solid"
        borderColor={borderColor}
        mx="auto"
      >
        <Spinner size="md" color="zs.accent" />
      </Flex>
    );
  }

  if (error || videoLoadError || !resolvedUrl) {
    return (
      <Box
        p={3}
        bg={errorBg}
        borderRadius="md"
        border="1px solid"
        borderColor="red.300"
        maxW="100%"
        w="300px"
        onClick={(e) => e.stopPropagation()}
        mx="auto"
      >
        <Flex align="center" direction="column" justify="center" textAlign="center" py={2}>
          <ChakraIcon as={FiAlertCircle} boxSize="28px" color="red.400" mb={2} />
          <Text fontSize="xs" fontWeight="semibold" mb={1}>
            Failed to Load Video
          </Text>
          <Text fontSize="10px" color="gray.500" mb={3} maxW="90%" textAlign="center">
            {getFriendlyErrorMessage(error)}
          </Text>
          <HStack spacing={2}>
            <Button
              size="xs"
              leftIcon={<ChakraIcon as={FiRefreshCw} />}
              onClick={handleRetry}
              colorScheme="blue"
              variant="outline"
            >
              Retry
            </Button>
            {(resolvedUrl || urlOrPath.startsWith("http")) && (
              <Button
                size="xs"
                leftIcon={<ChakraIcon as={FiExternalLink} />}
                onClick={handleOpenExternal}
                variant="ghost"
              >
                Open Tab
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>
    );
  }

  return (
    <video
      src={resolvedUrl}
      style={style}
      onError={() => {
        setVideoLoadError(true);
      }}
      {...props}
    />
  );
};

// ==========================================
// 3. RESOLVED AUDIO COMPONENT
// ==========================================
interface ResolvedAudioProps extends React.AudioHTMLAttributes<HTMLAudioElement> {
  urlOrPath: string;
}

export const ResolvedAudio: React.FC<ResolvedAudioProps> = ({
  urlOrPath,
  style,
  ...props
}) => {
  const [retryTrigger, setRetryTrigger] = useState(0);
  const { resolvedUrl, isLoading, error } = useResolveStorageUrl(urlOrPath, retryTrigger);
  const [audioLoadError, setAudioLoadError] = useState(false);

  const errorBg = useColorModeValue("gray.50", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    setAudioLoadError(false);
  }, [urlOrPath, retryTrigger]);

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRetryTrigger((prev) => prev + 1);
  };

  const handleOpenExternal = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(resolvedUrl || urlOrPath, "_blank");
  };

  if (isLoading) {
    return (
      <Flex align="center" justify="center" w="100%" py={4} mx="auto">
        <Spinner size="sm" color="zs.accent" />
      </Flex>
    );
  }

  if (error || audioLoadError || !resolvedUrl) {
    return (
      <Box
        p={3}
        bg={errorBg}
        borderRadius="md"
        border="1px solid"
        borderColor="red.300"
        maxW="100%"
        w="280px"
        onClick={(e) => e.stopPropagation()}
        mx="auto"
      >
        <Flex align="center" justify="space-between">
          <HStack spacing={2} flex={1} overflow="hidden">
            <ChakraIcon as={FiAlertCircle} color="red.400" flexShrink={0} />
            <Text fontSize="xs" fontWeight="semibold" isTruncated>
              {getFriendlyErrorMessage(error)}
            </Text>
          </HStack>
          <HStack spacing={1} ml={2}>
            <IconButton
              aria-label="Retry"
              icon={<ChakraIcon as={FiRefreshCw} />}
              size="xs"
              onClick={handleRetry}
              colorScheme="blue"
              variant="outline"
            />

          </HStack>
        </Flex>
      </Box>
    );
  }

  return (
    <audio
      src={resolvedUrl}
      onError={() => {
        setAudioLoadError(true);
      }}
      style={style}
      {...props}
    />
  );
};
